/* eslint-disable react/jsx-no-useless-fragment */
import { FC } from 'react';
import Moment from 'react-moment';
import { i18n } from '@lingui/core';
import { ExecutionStatus, WorkflowStepDTO } from '../../../Types/LogT';

const WorklowStepScheduleInfo: FC<{ workflowStep: WorkflowStepDTO; currentStep: boolean }> = ({
  workflowStep,
  currentStep,
}) => {
  const { procedureType, executionReport, executionConfig } = workflowStep;
  const { executionStatus, executedAt } = executionReport ?? {};
  const { thresholdDate, manualApprovalRequired, relativeAmount, relativeUnit, executionStrategy } = executionConfig;

  const relativeStr = `${relativeAmount} ${i18n._(`chronoUnit.${relativeUnit}`)}`;
  const strategyTranslation = `${i18n._(`execution.${executionStrategy}`)}`;

  if (currentStep && relativeAmount === 0 && !thresholdDate && !manualApprovalRequired) {
    return <>Schritt wird jetzt ausgeführt ...</>;
  }

  const renderExecutedAt = (prefix: string) =>
    executedAt ? (
      <>
        {prefix} <Moment fromNow date={executedAt} />
      </>
    ) : (
      <>{prefix}</>
    );

  const renderPlannedExecution = () => {
    if (manualApprovalRequired) {
      return <>Wartet auf manuelle Genehmigung</>;
    }

    if (thresholdDate) {
      if (procedureType === 'WAIT_UNTIL') {
        return (
          <>
            Wartet bis <Moment calendar date={thresholdDate} />
          </>
        );
      }

      return (
        <>
          Wird <Moment calendar date={thresholdDate} /> ausgeführt
        </>
      );
    }

    if (procedureType === 'WAIT_UNTIL') {
      return relativeAmount > 0 ? <>Wartet noch {relativeStr}</> : <>Wartet auf das Ereignis</>;
    }

    if (executionStrategy === 'AFTER_PREVIOUS_STEP') {
      return (
        <>
          {relativeAmount > 0
            ? `Wird ${relativeStr} nach dem vorherigen Schritt ausgeführt`
            : 'Wird direkt nach dem vorherigen Schritt ausgeführt'}
        </>
      );
    }

    return (
      <>
        {relativeAmount > 0
          ? `Wird ${relativeStr} ${strategyTranslation} ausgeführt`
          : `Wird ${strategyTranslation} ausgeführt`}
      </>
    );
  };

  const renderStatusInfo = (status?: ExecutionStatus) => {
    switch (status) {
      case 'FULFILLED':
        return renderExecutedAt(procedureType === 'WAIT_UNTIL' ? 'Wartebedingung erfüllt' : 'Bedingung erfüllt');
      case 'SKIPPED':
        return <>Schritt wurde übersprungen</>;
      case 'COMPLETED':
        return renderExecutedAt('Ausgeführt');
      case 'FAILED':
        return procedureType === 'WAIT_UNTIL' ? null : <>Ausführung fehlgeschlagen</>;
      case 'PRECONDITION_FAILED':
        return procedureType === 'WAIT_UNTIL' ? null : <>Voraussetzungen nicht erfüllt</>;
      case 'ABORTED':
        return procedureType === 'WAIT_UNTIL' ? null : <>Abgebrochen</>;
      case 'PAUSED':
        return <>Pausiert</>;
      case 'APPROVAL_REQUIRED':
        return <>Wartet auf manuelle Genehmigung</>;
      case 'WAITING':
      case 'SCHEDULED':
      default:
        return renderPlannedExecution();
    }
  };

  return renderStatusInfo(executionStatus);
};

export default WorklowStepScheduleInfo;
